import { Link, useHistory, useLocation } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { SubscriptionHelper } from 'view/helpers';

import { usePageTitle } from 'utils/pageTitle';
import { signUp, clearAuthErrors, signOut } from 'reducers/userReducer';
import { resolveInvitation } from 'data/repositories/invitationsRepository';

import Alert from 'components/Alert';
import Spinner from 'components/Spinner';

import './AuthPage.css';
import { AuthMapBackground } from 'view/marketing/components/site/auth-map-background';
import SmsAlertsOptInCheckbox, {
	isSmsOptInValid,
	phoneRequiresSmsOptIn,
} from 'view/components/SmsAlertsOptInCheckbox';
import { SocialLogins } from 'view/components/SocialLogin/SocialLogins';

/**
 * Error keys that have an input to render them under. Anything else is shown
 * as its own alert, so an error can never leave the banner pointing at
 * messages that are not on the page.
 */
const FIELD_ERROR_KEYS = ['email', 'name', 'password', 'phone', 'property'];

const ValidationMessages = ({ messages }) => {
	if (!messages?.length) {
		return null;
	}

	return messages.map((message) => (
		<div key={message} className="invalid-feedback">
			{message}
		</div>
	));
};

const SignUp = (props) => {
	usePageTitle('Sign Up');
	const history = useHistory();
	const location = useLocation();
	const dispatch = useDispatch();
	const { isSigningUp, signUpErrors, isLoggedIn } = useSelector((state) => ({
		isSigningUp: state.user.isSigningUp,
		signUpErrors: state.user.signUpErrors,
		isLoggedIn: !!state.user.userData?.id,
	}));
	// invitation_token, when present, is URL-encoded by AcceptInvitation before
	// it navigates here (see AcceptInvitation.js's goToSignUp); URLSearchParams
	// decodes it back for us, matching how SubscriptionHelper below already
	// reads other query params on this same page.
	const invitationToken = new URLSearchParams(location.search).get(
		'invitation_token',
	);

	const [name, setName] = useState('');
	const [email, setEmail] = useState('');
	const [password, setPassword] = useState('');
	const [phone, setPhone] = useState('');
	const [smsAlertsOptIn, setSmsAlertsOptIn] = useState(false);
	const [smsOptInError, setSmsOptInError] = useState(false);
	const [invitationDetails, setInvitationDetails] = useState(null);
	// const [property, setProperty] = useState('');
	// const onSubmit = () => dispatch(signUp(name, email, password, phone, property));

	/* eslint-disable react-hooks/exhaustive-deps */
	useEffect(() => {
		if (!invitationToken) {
			return;
		}

		// GET /api/invitations/{token} deliberately never returns the invited
		// email address (see OrganizationInvitationController::resolve()'s
		// docblock) so there is no address to pre-fill the email field with --
		// only the organization, role, and inviter are shown here to tell the
		// invitee what they are joining before they create an account. If this
		// fails (invalid token, network error), fall back to a plain signup
		// form rather than blocking; the token is still submitted and is
		// re-validated server-side on submit.
		dispatch(resolveInvitation(invitationToken))
			.then((result) => setInvitationDetails(result))
			.catch(() => setInvitationDetails(null));
	}, [invitationToken]);

	const onSubmit = () => {
		if (!isSmsOptInValid(phone, smsAlertsOptIn)) {
			setSmsOptInError(true);
			return;
		}

		setSmsOptInError(false);
		dispatch(
			signUp(name, email, password, phone, smsAlertsOptIn, invitationToken),
		)
			.then((res) => {
				if (res?.payload?.userData?.id) {
					//sign up success
					// Check if user came from checkout flow using helper
					const urlParams = new URLSearchParams(location.search);
					// The sign-up itself accepted any invitation it carried.
					const redirectUrl = SubscriptionHelper.getPostAuthRedirect(
						urlParams,
						{ invitationConsumed: true },
					);
					history.push(redirectUrl);
				}
			})
			.catch(() => {
				// The api layer rethrows after storing the error in signUpErrors,
				// which the form renders. Swallow it here so a rejected sign up does
				// not surface as an unhandled rejection.
			});
	};
	const onEnter = (event) => {
		if (event.key === 'Enter') {
			onSubmit();
		}
	};

	/* eslint-disable react-hooks/exhaustive-deps */
	useEffect(() => dispatch(clearAuthErrors()), []);

	useEffect(() => {
		if (isLoggedIn) {
			const urlParams = new URLSearchParams(location.search);
			const redirectUrl = SubscriptionHelper.getPostAuthRedirect(urlParams, {
				invitationConsumed: true,
			});
			history.push(redirectUrl);
		}
	}, [isLoggedIn, history, location.search]);

	const hasFieldErrors = Object.keys(signUpErrors).some((key) =>
		FIELD_ERROR_KEYS.includes(key),
	);
	const generalErrors = Object.entries(signUpErrors)
		.filter(([key]) => !FIELD_ERROR_KEYS.includes(key))
		.flatMap(([, messages]) => [].concat(messages ?? []));
	const canSubmit = isSmsOptInValid(phone, smsAlertsOptIn);
	const showSmsOptInError =
		smsOptInError || (phoneRequiresSmsOptIn(phone) && !smsAlertsOptIn);

	let alreadySignedIn = (
		<div className="sign-in-wrapper">
			<div className="alert alert-success">
				You're already signed in to your account!
			</div>
			<div className="input-wrapper">
				<Link to="/dashboard/overview">View your dashboard</Link>
			</div>
			<div className="input-wrapper">
				<Link to="/property">Find a property</Link>
			</div>
			<div className="input-wrapper">
				<Link to="/use-policy">Acceptable Use Policy</Link>
			</div>
			<div className="input-wrapper">
				<button
					onClick={() => dispatch(signOut())}
					className="btn btn-secondary"
				>
					Sign Out
				</button>
			</div>
		</div>
	);

	let signUpForm = (
		<div className="sign-in-wrapper">
			{invitationDetails && (
				<Alert color="info">
					<strong>{invitationDetails.invitedByName}</strong> invited you to
					join <strong>{invitationDetails.organizationName}</strong> as a{' '}
					<strong>{invitationDetails.role}</strong>. Sign up with the email
					address the invitation was sent to.
				</Alert>
			)}

			{hasFieldErrors && (
				<Alert
					color="danger"
					text="Please fix the errors below and try again"
				/>
			)}

			{generalErrors.map((message) => (
				<Alert color="danger" text={message} key={message} />
			))}

			<div className="input-wrapper">
				<label className="form-label">Email</label>
				<input
					type="text"
					value={email}
					onChange={(ev) => setEmail(ev.target.value)}
					className={`form-control ${signUpErrors.email ? ' is-invalid' : ''}`}
					placeholder="Email"
					disabled={isSigningUp}
					onKeyDown={onEnter}
					autoFocus
				/>
				<ValidationMessages messages={signUpErrors.email} />
			</div>

			<div className="input-wrapper">
				<label className="form-label">Name</label>
				<input
					type="text"
					value={name}
					onChange={(ev) => setName(ev.target.value)}
					className={`form-control ${signUpErrors.name ? ' is-invalid' : ''}`}
					placeholder="Full Name"
					disabled={isSigningUp}
					onKeyDown={onEnter}
				/>
				<ValidationMessages messages={signUpErrors.name} />
			</div>

			<div className="input-wrapper">
				<label className="form-label">Password</label>
				<input
					type="password"
					value={password}
					onChange={(ev) => setPassword(ev.target.value)}
					className={`form-control ${signUpErrors.password ? ' is-invalid' : ''
						}`}
					placeholder="Password"
					disabled={isSigningUp}
					onKeyDown={onEnter}
				/>
				<ValidationMessages messages={signUpErrors.password} />
			</div>

			<div className="input-wrapper">
				<label className="form-label">Phone (Optional)</label>
				<input
					type="text"
					value={phone}
					onChange={(ev) => {
						setPhone(ev.target.value);
						if (!phoneRequiresSmsOptIn(ev.target.value)) {
							setSmsOptInError(false);
						}
					}}
					className={`form-control ${signUpErrors.phone ? ' is-invalid' : ''}`}
					placeholder="Phone"
					disabled={isSigningUp}
					onKeyDown={onEnter}
				/>
				<ValidationMessages messages={signUpErrors.phone} />
			</div>

			<div className="input-wrapper">
				<SmsAlertsOptInCheckbox
					id="sms-alerts-opt-in"
					checked={smsAlertsOptIn}
					onChange={(ev) => {
						setSmsAlertsOptIn(ev.target.checked);
						if (ev.target.checked) {
							setSmsOptInError(false);
						}
					}}
					disabled={isSigningUp}
					showError={showSmsOptInError}
				/>
			</div>

			{/*			<div className="input-wrapper">
				<label className="form-label">Property Address</label>
				<input
					type="text"
					value={property}
					onChange={ev => setProperty(ev.target.value)}
					className={`form-control ${signUpErrors.property ? ' is-invalid' : ''}`}
					placeholder="Property Address"
					disabled={isSigningUp}
					onKeyDown={onEnter}
				/>
				<ValidationMessages messages={signUpErrors.property} />
			</div>*/}

			<div className="input-wrapper">
				<button
					onClick={onSubmit}
					disabled={isSigningUp || !canSubmit}
					className="btn btn-primary"
				>
					Sign Up
				</button>
			</div>

			<div className="input-wrapper">
				<Link to="/sign-in">Already have an account? Sign in</Link>
			</div>
			<div className="input-wrapper">
				<Link to="/use-policy">Acceptable Use Policy</Link>
			</div>

			<SocialLogins title="Continue" />
		</div>
	);

	return (
		<div className="auth-page" data-map-section>
			<AuthMapBackground />
			<div className="auth-page-content" data-map-protected>
				<h1>Sign Up</h1>
				{isSigningUp && <Spinner />}
				{isLoggedIn ? alreadySignedIn : signUpForm}
			</div>
		</div>
	);
};

export default SignUp;
